import { useApolloClient } from '@apollo/client';
import { Button, message } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    type CreateInventoryMutation,
    type CreateInventoryMutationVariables,
    CreateInventoryDocument,
} from '../../../api/mutations/createInventory';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import inventorySuccessModal from './InventorySuccessModal';
import SelectionSection from './SelectionSection';
import useInventoryValidator from './useInventoryValidator';
import { defaultValue, type FormValues } from './utils';

const AddInventoryPage = () => {
    const { t } = useTranslation('inventoryDetails');
    const navigate = useNavigate();
    const [requireDealer, setRequireDealer] = useState(true);
    const { setLoading, LoadingButton } = useLoadingButton();

    const validate = useInventoryValidator(requireDealer);
    const apolloClient = useApolloClient();

    const { open: openModal, render: renderModal } = inventorySuccessModal();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const settings = omit(['modelId', 'subModelId', 'variantId'], values);
            setLoading(true);
            message.loading({
                content: t('inventoryDetails:messages.addInventory.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateInventoryMutation, CreateInventoryMutationVariables>({
                    mutation: CreateInventoryDocument,
                    variables: {
                        settings,
                    },
                })
                .finally(() => {
                    setLoading(false);
                    message.destroy('primary');
                });

            openModal();
        },
        [apolloClient, openModal, setLoading, t]
    );

    const handleBack = useCallback(() => navigate('/admin/inventories'), [navigate]);

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="cancel" onClick={handleBack}>
                    {t('inventoryDetails:actions.cancel')}
                </Button>,
                <LoadingButton key="submit" form="createInventory" htmlType="submit" type="primary">
                    {t('inventoryDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={handleBack}
            title={t('inventoryDetails:titles.add')}
        >
            <Formik initialValues={defaultValue} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="createInventory" name="createInventory" onSubmitCapture={handleSubmit}>
                        <SelectionSection setRequireDealer={setRequireDealer} />
                        {renderModal()}
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddInventoryPage;
